import React, {useState, useEffect, useCallback} from 'react';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import Button from './Button';
import throttle from 'lodash.throttle'
import {theme} from '../lib/theme';
import {decs, units, addDecimals, toggleTooltip} from '../lib/utils';
import styled from 'styled-components';

const StyledChartScatter = styled.div`
  > div {
    position: relative;
    z-index: 7;
    padding: 0 0 32px 0;
    @media (min-width: 768px) {
      padding: 0 32px 32px 32px;
    }
  }

  .shadow {
    width: ${props => props.shadow[2]}px;
    background: #fff;
    -moz-box-shadow: 0 0 10px 0 rgba(51, 51, 51, .15);
    box-shadow: 0 0 10px 0 rgba(51, 51, 51, .15);
    position: absolute;
    height: ${props => props.shadow[3]}px;
    padding: 0;
    z-index: 5;
    top: ${props => props.shadow[0]}px;
    left: ${props => props.shadow[1]}px;
  }

  > button {
    width: 26px;
    height: auto;
    padding: 0;
    display: block;
    position: absolute;
    left: 0px;
    top: calc(100% - 213px);
    z-index: 9;
    > i {
      background-color: #21A0D2;
      width: 26px;
      height: 26px;
      padding: 3px;
      border-radius: 13px;
      margin: 0 auto;
      display: block;
    }
    > span {
      color: #555;
      font-size: 0.75em;
      letter-spacing: 1px;
      line-height: 12px;
      text-align: center;
      text-transform: uppercase;
      display: block;
      margin-top: 4px;
    }
    @media (min-width: 640px) {
      left: 16px;
      top: calc(100% - 208px);
    }
  }

  > span {
    display: block;
    position: absolute;
    color: #333;
    font-size: 8px;
    letter-spacing: 1px;
    line-height: 12px;
    text-align: right;
    width: auto;
    white-space: nowrap;
    padding: 0;
    z-index: 9998;
    cursor: default;
    &:first-of-type {
      bottom: 180px;
      right: 22px;
      @media (min-width: 640px) {
        right: 22px;
      }
      @media (min-width: 768px) {
        right: 52px;
      }
    }
    &:last-of-type {
      top: 26px;
      right: calc(100% - 8px);
      -moz-transform: rotate(-90deg);
      transform: rotate(-90deg); /*pixled text fix, but blurry: translate3d(0,0,0)*/
      -moz-transform-origin: right bottom;
      transform-origin: right bottom;
      @media (min-width: 640px) {
        right: calc(100% - 24px);
      }
    }
  }

  .apexcharts-canvas > svg,
  .apexcharts-canvas > svg > foreignObject {
    overflow: visible;
  }

  .apexcharts-xaxistooltip {
     display: none;
  }
  .apexcharts-yaxis-texts-g text,
  .apexcharts-xaxis-texts-g text {
    fill: #555;
    font-size: .825em;
  }

  .apexcharts-legend {
    min-height: 140px;
    left: -5px !important;
    display: inline-block;
    bottom: -40px !important;

    .apexcharts-legend-series {
      width: 50%;
      display: inline-block;
      padding: 0 4px 8px 0;
      float: left;
    }
    @media (min-width: 640px) {
      left: 36px !important;
      .apexcharts-legend-series {
        width: 33%;
      }
    }
  }

  .apexcharts-legend-marker {
    top: 1px !important;
    margin-right: 8px;
  }

  .apexcharts-marker.hover {
    stroke-width: 2;
    stroke: #000;
  }

  .apexcharts-yaxis line {
    stroke-width: 0;
  }

  .custom-tooltip {
    border-radius: 0 !important;
    padding: 10px 15px;
    font-size: .75em;
    white-space: nowrap;
    text-transform: uppercase;
    background: #fff;
    -moz-box-shadow: 0 0 10px 0 rgba(51, 51, 51, .15);
    box-shadow: 0 0 10px 0 rgba(51, 51, 51, .15);
    pointer-events: none;
  }

  .apexcharts-toolbar {
    max-width: unset;
    right: 10px;
    top: -20px !important;
    > div {
      transform: scale(1);
      width: 30px;
      height: 30px;
      border-radius: 2px;
      background-color: #FFFFFF;
      box-shadow: 0 0 6px 0 rgba(51,51,51,0.15);
      margin: 0 8px 0 0;
      min-width: 30px;
      -webkit-transition: all .3s ease-in-out;
      -moz-transition: all .3s ease-in-out;
      -o-transition: all .3s ease-in-out;
      transition: all .3s ease-in-out;
      > svg {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%,-50%);
      }
      &.apexcharts-selected {
        svg {
          transform: translate(-50%,-50%) scale(0.8);
        }
      }
      &:hover {
        box-shadow: 0 0 10px 0 rgba(51,51,51,0.3);
      }
      &.apexcharts-menu {
        width: auto;
        left: auto;
        right: 5px;
        top: 32px;
        height: auto;
      }
    }
    .hidden {
      display: none;
    }
    /* @media (min-width: 1024px) {
      right: -3px;
    } */
  }

  .apexcharts-element-hidden {
    display: none;
  }

  @media (min-width: 640px) {
    > span {
      font-size: 10px;
    }
  }
  @media (min-width: 1280px) {
    > span {
      font-size: 12px;
    }
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    .apexcharts-zoom-icon,
    .apexcharts-menu-icon{
    	display: none;
    }
  }
`;

export default function ChartScatter (props) {
  const {product, items, data, axis, scrolled, selected, toggleLayer} = props;
  const [mounted, setMounted] = useState();
  const [shadow, setShadow] = useState([]);
  const yMin = data
    .reduce((acc, curr) =>
        curr.data.reduce((acc2, curr2)  => [...acc2, curr2[1]], acc)
    , [])
    .reduce((acc, curr)  => curr < acc ? curr : acc, 100000);

  const [options, setOptions] = useState({
    xaxis: {
      type: "numeric",
      tickAmount: 6,
      tickPlacement: 'on',
      labels: {
        formatter: (val) =>  {
          const axDecs = decs[axis[0].replace(/ /g, '')] || 0;
          const str = val.toString().replace(/,/g, '.');
          const float = parseFloat(str);
          const rounded = float > 1000 ? 100 * Math.ceil(float / 100)
            : float > 100 ? 10 * Math.ceil(float / 10)
            : float > 10 ? Math.ceil(float)
            : float;
          const newStr = rounded.toFixed(axDecs);
          return newStr.substr(0,newStr.length-1)+"0";
          // const last = parseFloat(newStr.substr(-1));
          // const last2 = parseFloat(newStr.substr(-2));
          // const suffix = last > 5 ? last2 + (10-last) : last2 - last;
          // return newStr.substr(0,newStr.length-2)+suffix;
        }
      },
      axisBorder: {
        show: false,
        color: '#FFFFFF',
        offsetX: 0,
        offsetY: 0,
        height: 4
      },
      axisTicks: {
        show: false
      },
    },
    yaxis: {
      type: "numeric",
      min: yMin,
      tickAmount: 5,
      labels: {
        formatter: (val) => {
          const axDecs = decs[axis[1].replace(/ /g, '')] || 0;
          const str = val.toString().replace(/,/g, '.');
          const float = parseFloat(str);
          const rounded = float > 1000 ? 100 * Math.ceil(float / 100)
            : float > 100 ? 10 * Math.ceil(float / 10)
            : float > 10 ? Math.ceil(float)
            : float;
            const newStr = rounded.toFixed(axDecs);
            return newStr.substr(0,newStr.length-1)+"0";
            // const last = parseFloat(newStr.substr(-1));
            // const last2 = parseFloat(newStr.substr(-2));
            // const suffix = last > 5 ? last2 + (10-last) : last2 - last;
            // return newStr.substr(0,newStr.length-2)+suffix;
        }
      },
      axisBorder: {
        show: false,
        color: '#FFFFFF',
        offsetX: 0,
        offsetY: 0,
      },
      axisTicks: {
        show: false
      },
    },
    markers: {
      size: 7,
      strokeColors: 'transparent',
      strokeWidth: 0,
      strokeOpacity: 1,
      fillOpacity: 1,
      discrete: [],
      shape: "circle",
      radius: 3.5,
      offsetX: 0,
      offsetY: 0,
      onClick: undefined,//(e)=>{console.log(data[parseInt(e.target.getAttribute("index"))].data[parseInt(e.target.getAttribute("rel"))])},
      onDblClick: undefined,
      hover: {
        sizeOffset: 3,
        fillOpacity: 1,
      }
    },
    grid: {
      show: true,
      borderColor: '#FFF',
      strokeDashArray: 0,
      position: 'back',
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true,
          stroke: '#FFF',
        }
      },
      column: {
        colors: ['#F8F8F8', '#F2F2F2'],
        opacity: 1
      },
    },
    chart: {
      type: 'scatter',
      floating: false,
      //height: window.outerWidth > 1024 ? `${window.innerHeight - (scrolled ? 100 : 300)}` : "700",
      zoom: {
        enabled: true,
        type: 'xy',
        zoomedArea: {
          fill: {
            color: '#000000',
            opacity: 0.2,
          },
          stroke: {
            color: '#000000',
            opacity: 1,
            width: 1,
          }
        },
        events: {
          zoomed: (chartContext, { xaxis, yaxis }) => {
            //console.log(chartContext, { xaxis, yaxis })
          }
        }
      },
      toolbar: {
        show: true,
        tools: {
          download:
            `<svg className="download" width="16px" height="17px" viewBox="0 0 16 17" version="1.1">
              <g transform="translate(0.500000, 0.750000)" fill="none">
                <polyline stroke="#BBBBBB" points="3.8013697 3.69862998 0.102739725 3.69862998 0.102739725 14.7945203 14.8972596 14.7945203 14.8972596 3.69862998 11.1986297 3.69862998" vectorEffect="non-scaling-stroke"></polyline>
                <polyline stroke="#333333" points="3.80137002 7.39725995 7.5 11.0958899 11.19863 7.39725995" vectorEffect="non-scaling-stroke"></polyline>
                <path d="M7.49999975,10.5266569 L7.49999975,0" stroke="#333333" vectorEffect="non-scaling-stroke"></path>
              </g>
            </svg>`,
          selection:
            `<svg className="selection" width="22px" height="22px" viewBox="0 0 22 22" version="1.1">
              <g transform="translate(1.250000, 1.250000)" fill="none">
                <polygon fill="#666666" stroke="none" points="14.9205608 20.7617476 16.8676013 16.8676376 20.7615294 14.9205825 12 12"></polygon>
                <polyline stroke="#BBBBBB" stroke-linecap="square" points="8.89259666 14.2281547 -8.52651283e-14 14.2281547 -8.52651283e-14 0 14.2281547 0 14.2281547 8.89259666"></polyline>
              </g>
            </svg>`,
          zoom:
            `<svg className="selection" width="22px" height="22px" viewBox="0 0 22 22" version="1.1">
              <g transform="translate(1.250000, 1.250000)" fill="none">
                <polygon fill="#666666" stroke="none" points="14.9205608 20.7617476 16.8676013 16.8676376 20.7615294 14.9205825 12 12"></polygon>
                <polyline stroke="#BBBBBB" stroke-linecap="square" points="8.89259666 14.2281547 -8.52651283e-14 14.2281547 -8.52651283e-14 0 14.2281547 0 14.2281547 8.89259666"></polyline>
              </g>
            </svg>`,
          zoomin:
            `<svg className="zoom-in" width="17px" height="17px" viewBox="0 0 17 17" version="1.1">
              <g transform="translate(1.250000, 1.250000)" fill="none">
                <path d="M13.2621954,6.67681113 C13.2621954,10.3137624 10.3137807,13.2621771 6.67682943,13.2621771 C3.03987812,13.2621771 0.0914634168,10.3137624 0.0914634168,6.67681113 C0.0914634168,3.03985983 3.03987812,0.0914451219 6.67682943,0.0914451219 C10.3137807,0.0914451219 13.2621954,3.03985983 13.2621954,6.67681113 Z" stroke="#BBBBBB" vectorEffect="non-scaling-stroke"></path>
                <path d="M11.3171704,11.3171525 L14.9085728,14.9085549" stroke="#BBBBBB" vectorEffect="non-scaling-stroke"></path>
                <path d="M6.67682943,3.38412809 L6.67682943,9.9694941" stroke="#333333" vectorEffect="non-scaling-stroke"></path>
                <path d="M3.38414633,6.67681083 L9.96951234,6.67681083" stroke="#333333" vectorEffect="non-scaling-stroke"></path>
              </g>
            </svg>`,
          zoomout:
            `<svg className="zoom-out" width="17px" height="17px" viewBox="0 0 17 17" version="1.1">
              <g transform="translate(1.250000, 1.250000)" fill="none">
                  <path d="M13.2621954,6.67681113 C13.2621954,10.3137624 10.3137807,13.2621771 6.67682943,13.2621771 C3.03987812,13.2621771 0.0914634168,10.3137624 0.0914634168,6.67681113 C0.0914634168,3.03985983 3.03987812,0.0914451219 6.67682943,0.0914451219 C10.3137807,0.0914451219 13.2621954,3.03985983 13.2621954,6.67681113 Z" stroke="#BBBBBB" vectorEffect="non-scaling-stroke"></path>
                  <path d="M11.3171704,11.3171525 L14.9085728,14.9085549" stroke="#BBBBBB" vectorEffect="non-scaling-stroke"></path>
                  <path d="M3.38414633,6.67681083 L9.96951234,6.67681083" stroke="#333333" vectorEffect="non-scaling-stroke"></path>
              </g>
            </svg>`,
          pan: false,
          reset:
            `<svg cclassName="reset" width="18px" height="16px" viewBox="0 0 18 16" version="1.1">
              <g transform="translate(0, 0.50000)" fill="none">
                <path d="M16.2000003,7.30000019 C16.2000003,11.2764003 12.9764002,14.5000005 9.00000019,14.5000005 C6.84860016,14.5000005 4.91780013,13.5566004 3.59840012,12.0608004" stroke="#BBBBBB" stroke-linecap="square" vectorEffect="non-scaling-stroke"></path>
                <path d="M1.80000007,7.30000029 C1.80000007,3.32360013 5.02359995,0.100000005 8.99999981,0.100000005 C11.1625997,0.100000005 13.1025997,1.05340004 14.4221996,2.5628001" stroke="#BBBBBB" stroke-linecap="square" vectorEffect="non-scaling-stroke"></path>
                <polygon fill="#BBBBBB" points="0 7.30000019 3.60000014 7.30000019 1.80000007 10.9000003" vectorEffect="non-scaling-stroke"></polygon>
                <polygon fill="#BBBBBB" points="14.4000006 7.30000019 18.0000007 7.30000019 16.2000006 3.70000005" vectorEffect="non-scaling-stroke"></polygon>
              </g>
            </svg>`,
          customIcons: []
        },
        autoSelected: 'zoom'
      },
    },
    tooltip: {
      custom: (data) =>  {
        return getTooltip(data);
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      floating: false,
      fontSize: '13px',
      fontFamily: 'Helvetica, Arial',
      offsetY: -44,
      offsetX: 16,
      labels: {
          colors: ['#BBB'],
          useSeriesColors: true
      },
      itemMargin: {
        horizontal: 2,
        vertical: "14px"
      },
      markers: {
        width: 14,
        height: 14,
        radius: 14,
      },
    },
    colors: [theme.blueDark, theme.blueBright, theme.greenDark, theme.greenBright, theme.orange, theme.red, theme.purple, theme.yellow],

  })

  const getProduct = (curr) => {
    const product = items.filter(item => {
      const currXvalue = data[curr.seriesIndex].data[curr.dataPointIndex][0];
      const currYvalue = data[curr.seriesIndex].data[curr.dataPointIndex][1];
      const currXProp = item.properties.filter(prop => prop.label === axis[0] && (prop.value === currXvalue || (prop.value === -1 && currXvalue === 0)))[0];
      const currYProp = item.properties.filter(prop => prop.label === axis[1] && (prop.value === currYvalue || (prop.value === -1 && currYvalue === 0)))[0];
      const match = item.properties.some(prop => prop.value === data[curr.seriesIndex].name) && currXProp && currYProp
      return match
    });
    return product[0] || {};
  }

  const markDot = useCallback((product) => {
    const dots = document.querySelectorAll(".apexcharts-marker");
    if(typeof product !== "string"){
      dots.forEach(dot => {
        dot.setAttribute("r", 7);
        dot.classList.remove("hover");
      });
    }
    if(!!product){
      const currProduct = typeof product === "string" ? items.filter(item => item.id === product)[0] : product;
      if(!currProduct){
        return
      }
      const chemistry = currProduct.properties.filter(prop => prop.key === "Chemistry")[0].value;
      const xProp = currProduct.properties.filter(prop => prop.label === axis[0])[0];
      const yProp = currProduct.properties.filter(prop => prop.label === axis[1])[0];
      const xValue = !xProp.value || xProp.value === -1 ? 0 : xProp.value;
      const yValue = !yProp.value || yProp.value === -1 ? 0 : yProp.value;;
      const seriesIndex = data.map((item, index) => {
        if(item.name === chemistry){
          return index;
        } else {
          return null;
        }
      }).filter(item => item !== null)[0];
      const dataPointIndex = !data[seriesIndex] ? [] : data[seriesIndex].data.map((arr, index) => {
        if(arr[0] === xValue && arr[1] === yValue){
          return index;
        } else {
          return null;
        }
      }).filter(item => !!item && item >= 0)[0] || 0;

      let dot;
      for(let i = 0; i < dots.length; i++){
        const hasIndex = parseInt(dots[i].getAttribute("index")) === seriesIndex;
        const hasValues = parseInt(dots[i].getAttribute("rel")) === dataPointIndex;
        if(hasIndex && hasValues){
          dot = dots[i];
          break;
        }
      };
      if(dot){
        typeof product !== "string" && dot.setAttribute("r", 7);
        dot.classList.add("hover");
      }
    }
  }, [data, axis, items]);

  const getTooltip = (curr) => {
    const product = getProduct(curr);
    return `<div class="custom-tooltip">${product.name}</div>`;
  }

  const onResize = () => {
    const shadow = document.getElementsByClassName("shadow")[0];
    const chart = shadow ? shadow.parentElement.getBoundingClientRect() : {};
    const area = document.getElementsByClassName("apexcharts-grid")[0];
    const sWidth = area ? area.getBoundingClientRect().width + 4 : "auto";
    const sHeight = area ? area.getBoundingClientRect().height + 4: "auto";
    const left = area ? area.getBoundingClientRect().left - chart.left - 4 : 0;
    const top = area ? area.getBoundingClientRect().top - chart.top : 0;
    setShadow([top, left, sWidth, sHeight]);
  }

  useEffect(() => {
    if(!mounted){
      setMounted(true);
      onResize();
    }
  }, [axis, mounted]);

  useEffect(() => {
    markDot(product);
    !!selected.length && selected.forEach(item => {markDot(item)});
  }, [product, markDot, selected]);

  const xUnit = units[axis[0]] ? `(${units[axis[0]]})` : "";
  const yUnit = units[axis[1]] ? `(${units[axis[1]]})` : "";
  return (
    <StyledChartScatter shadow={shadow}>
      <Button label="edit" icon="edit" transparent onClick={toggleLayer}/>
      <span
        onMouseEnter={(e) => toggleTooltip(e, {title: axis[0]})}
        onMouseLeave={(e) => toggleTooltip()}
      >
        {axis[0].toUpperCase()} {xUnit}</span>
      <span
        onMouseEnter={(e) => toggleTooltip(e, {title: axis[1]})}
        onMouseLeave={(e) => toggleTooltip()}
      >
        {axis[1].toUpperCase()} {yUnit}
      </span>
      <Chart
        options={options}
        series={data}
        type="scatter"
        height={window.outerWidth > 1024 ? `${window.innerHeight - (scrolled ? 150 : 300)}` : 500}
      />
      <div className="shadow"></div>
    </StyledChartScatter>
  )
}

ChartScatter.propTypes = {
  options: PropTypes.object,
  data: PropTypes.array,
};
